import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2, Upload, Loader2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { COMPANY, PRODUCTS, IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-brand-charcoal outline-none transition-colors focus:border-brand-red placeholder:text-slate-400";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="mb-1.5 block font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">{label}</span>
        {children}
    </label>
);

const Contact = () => {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", industry: "", product: "", process: "", message: "" });
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (file) fd.append("attachment", file);
            await axios.post(`${API}/enquiries`, fd);
            setStatus("sent");
        } catch {
            setStatus("error");
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHero
                kicker="CONTACT"
                title="Talk to Our"
                accent="Engineering Team"
                sub="Share your process requirement — we will help define the right furnace, heating and automation solution."
                image={IMAGES.plc}
                crumb="Contact"
            />
            <section className="bg-brand-mist py-20 md:py-24 technical-grid-light">
                <div className="mx-auto grid max-w-[1440px] gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-10">
                    <div className="space-y-6 lg:col-span-2">
                        <Reveal>
                            <div className="bg-brand-ink p-8">
                                <Kicker light>SS ENGINEERING WORKS</Kicker>
                                <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-brand-ember uppercase">{COMPANY.tagline}</p>
                                <ul className="mt-8 space-y-6 text-sm">
                                    <li className="flex gap-4">
                                        <MapPin size={19} className="mt-0.5 shrink-0 text-brand-red" />
                                        <div>
                                            <p className="font-display font-bold text-white">Works & Office</p>
                                            <p className="mt-1 leading-relaxed text-slate-400">{COMPANY.address}</p>
                                        </div>
                                    </li>
                                    <li className="flex gap-4">
                                        <Phone size={19} className="mt-0.5 shrink-0 text-brand-red" />
                                        <div>
                                            <p className="font-display font-bold text-white">Phone</p>
                                            <a href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`} data-testid="contact-phone-1" className="mt-1 block text-slate-400 hover:text-brand-ember transition-colors">{COMPANY.phone1}</a>
                                            <a href={`tel:${COMPANY.phone2.replace(/\s/g, "")}`} data-testid="contact-phone-2" className="block text-slate-400 hover:text-brand-ember transition-colors">{COMPANY.phone2}</a>
                                        </div>
                                    </li>
                                    <li className="flex gap-4">
                                        <Mail size={19} className="mt-0.5 shrink-0 text-brand-red" />
                                        <div>
                                            <p className="font-display font-bold text-white">Email</p>
                                            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="mt-1 block break-all text-slate-400 hover:text-brand-ember transition-colors">{COMPANY.email}</a>
                                        </div>
                                    </li>
                                </ul>
                                <p className="mt-8 border-t border-brand-line pt-5 font-mono text-[10px] tracking-[0.16em] text-slate-500">GSTIN: {COMPANY.gstin}</p>
                                <div className="mt-6 flex gap-3">
                                    <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
                                        className="flex flex-1 items-center justify-center gap-2 bg-[#25D366] px-4 py-3 font-display text-xs font-bold tracking-[0.12em] text-white transition-opacity hover:opacity-90">
                                        <MessageCircle size={15} /> WHATSAPP
                                    </a>
                                    <a href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`} data-testid="contact-call"
                                        className="flex flex-1 items-center justify-center gap-2 bg-brand-red px-4 py-3 font-display text-xs font-bold tracking-[0.12em] text-white transition-colors hover:bg-brand-ember">
                                        <Phone size={15} /> CALL
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="overflow-hidden border border-slate-200">
                                <iframe
                                    title="SS Engineering Works location — Greater Noida"
                                    src="https://www.google.com/maps?q=Brindavan+Garden,+Greater+Noida,+Uttar+Pradesh+201318,+India&output=embed"
                                    className="h-64 w-full border-0"
                                    loading="lazy"
                                    data-testid="contact-map"
                                />
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1} className="lg:col-span-3">
                        <div className="bg-white p-8 shadow-[0_2px_20px_rgba(15,18,22,0.05)] md:p-10">
                            {status === "sent" ? (
                                <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success">
                                    <CheckCircle2 size={52} strokeWidth={1.2} className="text-brand-red" />
                                    <h2 className="mt-6 font-display text-2xl font-extrabold uppercase text-brand-charcoal">Enquiry Received</h2>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">
                                        Thank you. Your enquiry has been received. Our team will review your requirements and contact you.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submit} data-testid="contact-form">
                                    <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-brand-charcoal">Send Enquiry</h2>
                                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                                        <Field label="Name *"><input required data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" /></Field>
                                        <Field label="Company"><input data-testid="contact-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" /></Field>
                                        <Field label="Email *"><input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" /></Field>
                                        <Field label="Phone *"><input required data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 ..." /></Field>
                                        <Field label="Industry"><input data-testid="contact-industry" value={form.industry} onChange={set("industry")} className={inputCls} placeholder="e.g. Automotive" /></Field>
                                        <Field label="Product / Solution Required">
                                            <select data-testid="contact-product" value={form.product} onChange={set("product")} className={inputCls}>
                                                <option value="">Select a product</option>
                                                {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                                                <option value="Other">Other / Custom</option>
                                            </select>
                                        </Field>
                                        <div className="sm:col-span-2">
                                            <Field label="Required Temperature / Process"><input data-testid="contact-process" value={form.process} onChange={set("process")} className={inputCls} placeholder="e.g. 950°C hardening cycle" /></Field>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Field label="Message"><textarea rows={4} data-testid="contact-message" value={form.message} onChange={set("message")} className={inputCls} placeholder="Describe your requirement" /></Field>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <span className="mb-1.5 block font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">Upload Requirement / RFQ</span>
                                            <label className="flex cursor-pointer items-center gap-3 border border-dashed border-slate-300 px-4 py-3.5 text-sm text-slate-500 transition-colors hover:border-brand-red" data-testid="contact-upload">
                                                <Upload size={16} className="text-brand-red" />
                                                {file ? file.name : "Attach drawing or RFQ document"}
                                                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
                                            </label>
                                        </div>
                                    </div>
                                    {status === "error" && (
                                        <p className="mt-4 text-sm text-brand-red" data-testid="contact-error">Submission failed. Please try again or contact us directly by phone.</p>
                                    )}
                                    <button type="submit" disabled={status === "sending"} data-testid="contact-submit"
                                        className="mt-8 flex w-full items-center justify-center gap-3 bg-brand-red px-8 py-4 font-display text-sm font-bold tracking-[0.16em] text-white transition-colors hover:bg-brand-charcoal disabled:opacity-60 sm:w-auto">
                                        {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : null}
                                        SEND ENQUIRY
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Contact;
